import React, { useCallback, useMemo, useState } from "react";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import {
    makeStyles,
    SearchBox,
    SearchBoxChangeEvent,
    InputOnChangeData,
    Text,
    tokens,
} from "@fluentui/react-components";
import { ArrowClockwise20Regular } from "@fluentui/react-icons";
import { Project } from "../../models/ProjectsResponse";
import { NewformaLink } from "../_base/Link";
import { NewformaSpinner } from "../_base/Spinner";
import { ProjectRadiosList, NoneFound, getSortedProjects } from "./ProjectRadiosFluent9";

const MAX_VISIBLE_PROJECTS = 15;

export interface SendAndFilePaginatedProjectAreaProps extends LocalizeContextProps {
    isDarkTheme: boolean;
    useGlobalProjects: boolean;
    myProjects: Project[];
    globalProjects: Project[];
    suggestedProjects: Project[];
    isLoadingMyProjects: boolean;
    isLoadingGlobalProjects: boolean;
    isReloading: boolean;
    selectedProject: Project | null;
    onProjectSelected: (project: Project | null) => void;
    onRefresh: () => void;
    itemLabel: (project: Project) => string;
    isProjectFilterMatch: (project: Project, filterString: string) => boolean;
}

const useStyles = makeStyles({
    searchContainer: {
        fontSize: tokens.fontSizeBase300,
    },
    searchInputRow: {
        marginTop: "8px",
    },
    searchBox: {
        width: "100%",
        "border-bottom-color": tokens.colorNeutralStrokeAccessible,
        "&&&:hover": {
            "border-color": tokens.colorNeutralStrokeAccessible,
        },
    },
    searchHintRow: {
        marginTop: "4px",
        display: "flex",
        alignItems: "center",
        gap: "6px",
    },
    refreshLinkRow: {
        display: "flex",
        marginTop: tokens.spacingVerticalS,
        marginBottom: tokens.spacingVerticalS,
        marginLeft: "auto",
    },
    refreshLink: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: tokens.fontSizeBase200,
        marginLeft: "auto",
    },
});

const SendAndFilePaginatedProjectArea = (props: SendAndFilePaginatedProjectAreaProps): JSX.Element => {
    const styles = useStyles();
    const [filterString, setFilterString] = useState("");
    const isDarkMode = document.querySelector("[data-theme]")?.getAttribute("data-theme") !== "default";

    const isLoading = props.isLoadingMyProjects;

    const handleSearchChange = useCallback(
        (_ev: SearchBoxChangeEvent, data: InputOnChangeData) => {
            const value = data.value ?? "";
            setFilterString(value);
            if (props.selectedProject) {
                props.onProjectSelected(null);
            }
        },
        [props.selectedProject, props.onProjectSelected]
    );

    const handleSearchDismiss = useCallback(() => {
        setFilterString("");
        if (props.selectedProject) {
            props.onProjectSelected(null);
        }
    }, [props.selectedProject, props.onProjectSelected]);

    const handleRefresh = useCallback(() => {
        setFilterString("");
        props.onRefresh();
    }, [props.onRefresh]);

    const placeholderText = props.isLoadingMyProjects
        ? (props.translate("SEND_AND_FILE_NEW.LOADING_PROJECTS") as string)
        : props.useGlobalProjects
        ? (props.translate("SEND_AND_FILE_NEW.SEARCH_PROJECTS") as string)
        : (props.translate("SEND_AND_FILE_NEW.SEARCH_MYPROJECTS") as string);

    // Determine which projects to search through
    const allProjects = useMemo(() => (props.useGlobalProjects ? props.globalProjects : props.myProjects), [
        props.useGlobalProjects,
        props.globalProjects,
        props.myProjects,
    ]);

    const normalizedFilter = filterString.toLocaleLowerCase();

    // Render the hint message below the search input
    const renderStatusMessage = (): JSX.Element => {
        if (props.isLoadingMyProjects || props.isLoadingGlobalProjects) {
            const messageKey = props.isReloading
                ? "SEND_AND_FILE_NEW.RELOADING_PROJECTS"
                : "SEND_AND_FILE_NEW.LOADING_PROJECTS";
            return (
                <div className={styles.searchHintRow}>
                    <NewformaSpinner size="tiny" />
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                        {props.translate(messageKey)}
                    </Text>
                </div>
            );
        }
        return (
            <div className={styles.searchHintRow}>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                    {props.translate("SEND_AND_FILE_NEW.USE_SEARCH")}
                </Text>
            </div>
        );
    };

    // Memoize filtered project list to avoid recomputing on every render
    const filteredProjectList = useMemo(() => {
        if (!normalizedFilter) return [];

        const suggestedNrns = new Set(props.suggestedProjects.map((p) => p.nrn));
        const myProjectNrns = new Set(props.myProjects.map((p) => p.nrn));

        // Filter each group (small result sets), then sort, then take up to MAX total
        const filteredSuggested = getSortedProjects(
            props.suggestedProjects.filter((p) => props.isProjectFilterMatch(p, normalizedFilter)),
            props.itemLabel
        );

        const filteredMy = getSortedProjects(
            props.myProjects.filter(
                (p) => !suggestedNrns.has(p.nrn) && props.isProjectFilterMatch(p, normalizedFilter)
            ),
            props.itemLabel
        );

        const remaining = MAX_VISIBLE_PROJECTS - filteredSuggested.length - filteredMy.length;
        let filteredGlobal: Project[] = [];
        if (remaining > 0) {
            // Only collect up to `remaining` matches from global to avoid sorting a huge array
            const globalMatches: Project[] = [];
            for (const project of allProjects) {
                if (globalMatches.length >= remaining) break;
                if (
                    !suggestedNrns.has(project.nrn) &&
                    !myProjectNrns.has(project.nrn) &&
                    props.isProjectFilterMatch(project, normalizedFilter)
                ) {
                    globalMatches.push(project);
                }
            }
            filteredGlobal = getSortedProjects(globalMatches, props.itemLabel);
        }

        return [
            ...filteredSuggested.slice(0, MAX_VISIBLE_PROJECTS),
            ...filteredMy.slice(0, MAX_VISIBLE_PROJECTS - filteredSuggested.length),
            ...filteredGlobal,
        ];
    }, [
        normalizedFilter,
        props.suggestedProjects,
        props.myProjects,
        allProjects,
        props.isProjectFilterMatch,
        props.itemLabel,
    ]);

    // Render project list based on filter state
    const renderProjectList = (): JSX.Element => {
        if (!normalizedFilter) {
            return (
                <ProjectRadiosList
                    groups={[
                        {
                            title: props.translate("SEND_AND_FILE_NEW.SUGGESTED_PROJECTS") as string,
                            items: props.suggestedProjects,
                            isVisibleIfEmpty: false,
                        },
                        {
                            title: props.translate("SEND_AND_FILE_NEW.MY_PROJECTS") as string,
                            items: props.myProjects.slice(0, MAX_VISIBLE_PROJECTS),
                            isVisibleIfEmpty: true,
                        },
                    ]}
                    filter=""
                    onSelectionChanged={props.onProjectSelected}
                    itemLabel={props.itemLabel}
                    isLoading={props.isLoadingMyProjects}
                    isDarkTheme={props.isDarkTheme}
                    selectedItem={props.selectedProject}
                />
            );
        }

        const combinedFiltered = filteredProjectList;

        if (!combinedFiltered.length) {
            return <NoneFound isDarkTheme={props.isDarkTheme} useGlobalProjects={props.useGlobalProjects} />;
        }

        return (
            <ProjectRadiosList
                groups={[
                    {
                        title: props.translate("SEND_AND_FILE_NEW.SEARCH_RESULTS") as string,
                        items: combinedFiltered.slice(0, MAX_VISIBLE_PROJECTS),
                        isVisibleIfEmpty: false,
                    },
                ]}
                filter={normalizedFilter}
                onSelectionChanged={props.onProjectSelected}
                itemLabel={props.itemLabel}
                isLoading={props.isLoadingMyProjects}
                isDarkTheme={props.isDarkTheme}
                selectedItem={props.selectedProject}
            />
        );
    };

    return (
        <>
            <div>
                <Text size={300}>{props.translate("SEND_AND_FILE_NEW.FILING_HINT") as string}</Text>
                <div className={styles.searchContainer}>
                    <div className={styles.searchInputRow}>
                        <SearchBox
                            data-testid="paginated-project-search"
                            value={filterString}
                            placeholder={placeholderText}
                            onChange={handleSearchChange}
                            dismiss={{ onClick: handleSearchDismiss }}
                            disabled={props.isLoadingMyProjects}
                            className={styles.searchBox}
                            style={{ backgroundColor: isDarkMode ? tokens.colorNeutralBackground3 : "" }}
                        />
                    </div>
                    {renderStatusMessage()}
                    <div className={styles.refreshLinkRow}>
                        <NewformaLink
                            disabled={isLoading || props.isLoadingGlobalProjects}
                            onClick={handleRefresh}
                            className={styles.refreshLink}
                            dataTestId="reload-projects-button"
                        >
                            <ArrowClockwise20Regular />
                            {props.translate("SEND_AND_FILE_NEW.RELOAD_PROJECTS") as string}
                        </NewformaLink>
                    </div>
                </div>
            </div>
            {renderProjectList()}
        </>
    );
};

export default withLocalize(SendAndFilePaginatedProjectArea);
